/**
 * Mirrors the IAM user-management API payloads.
 * Field shapes follow the Notion spec verbatim; the request interceptor
 * already converts snake_case ↔ camelCase, so client types use camelCase.
 */

export type AccountLevel = 'SUPER_ADMIN' | 'TENANT_ADMIN' | 'USER'

export const ACCOUNT_LEVEL_VALUES: AccountLevel[] = ['SUPER_ADMIN', 'TENANT_ADMIN', 'USER']

export interface UserDTO {
  id: string
  tenantId: string | null
  username: string
  email: string | null
  fullName: string | null
  avatarUrl: string | null
  phone?: string | null
  accountLevel: AccountLevel | null
  isActive: boolean
  createdAt: string
}

/**
 * An admin account as returned by the real IAM API. Unlike the mock-backed `UserDTO`,
 * `accountLevel` arrives in the backend's lowercase form (e.g. `tenant_admin`).
 */
export interface TenantAdminDTO extends Omit<UserDTO, 'accountLevel'> {
  accountLevel: string | null
  /** Head admin of the tenant — the only admin (besides a super admin) allowed to add admins. */
  isTenantOwner: boolean
}

export interface TenantAdminCreateRequest {
  username: string
  password: string
  email?: string | null
  fullName?: string | null
  avatarUrl?: string | null
}

export interface UserCreateRequest {
  username: string
  password: string
  email?: string | null
  fullName?: string | null
  avatarUrl?: string | null
  phone?: string | null
}

export interface UserUpdateRequest {
  fullName?: string | null
  email?: string | null
  avatarUrl?: string | null
  phone?: string | null
}

export interface ChangePasswordRequest {
  newPassword: string
}

export interface UserAccountLevelUpdateRequest {
  accountLevel: AccountLevel
}

export interface ResetPasswordResponseDTO {
  userId: string
  temporaryPassword: string
}

export interface PaginatedDTO<T> {
  items: T[]
  total: number
  skip: number
  limit: number
}

export type IUser = UserDTO

export interface UserListParams {
  skip: number
  limit: number
  search?: string
  accountLevel?: AccountLevel
  isActive?: boolean
}
